import { useRef, useMemo, useState, useEffect } from 'react';
import { getGameInfoByIdPlus } from '@intelligence/services/common';
import { fetchMetricDataGames } from '@intelligence/v3/services/topCharts';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getDatesByDateRange } from '@intelligence/v3/utils/date';
import { analysisSortMetricKey, getGranularityAUMetricKey } from '@intelligence/v3/utils/metric';
import { getTopList } from '@intelligence/v3/utils/topcharts';
import { handleTableDefaultSelectGames, filterGamesWithRank, sortTableByMetric } from '@intelligence/utils/tableUtils';
import {
  URL_CHART_TYPE,
  URL_FILTER_KEY,
  updateShortLinkFilterId,
  updateTimeFilterValue,
} from '@intelligence/utils/urlPathCfg';
import { convertParams } from '../_services/utils';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import { PAGINATION_FETCH_PARAMS } from '@intelligence/v3/constants';
import { TOPGAMENUM } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import { IntelligenceRequestAt } from '@intelligence/enums/common';
import { MOBILE_TOPCHARTS_TAG_DETAIL_FIELDS } from '@intelligence/v3/utils/sensorTowerTagColumns';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IUseTopChartsCoreProps, IUseTopChartsCoreResponse } from './types';

const FIXED_SELECTION_DEFAULT: string[] = [];

/**
 * @description 榜单核心逻辑hooks，该hooks内处理了，页面初始化查询、排序、翻页等关联逻辑
 * @param props 配置，具体定义参见IUseTopChartsCoreProps
 */
export default function useTopChartsCore<E extends IMetricDataGame>(
  props: IUseTopChartsCoreProps,
): IUseTopChartsCoreResponse<E> {
  const {
    metrics,
    hasUrlShortLink = false,
    sortByFront = false,
    fixedSelection = FIXED_SELECTION_DEFAULT,
    primaryKey = 'app_id',
    defaultSelectGameNum = TOPGAMENUM,
    defaultParams = {},
    shortLinkValidQuery,
    filterValue,
    setFilterVale,
    onGetLineChartDataSuccess,
    onNoGameExtend,
  } = props;

  // echarts图表类型（url上指定）
  const chartTypeUrl = (shortLinkValidQuery?.[URL_CHART_TYPE] as ChartType) ?? ChartType.Bar;

  // 查询表单对象
  const [fetchParams, setFetchParams] = useState(() => ({ ...defaultParams }));
  // echarts 图表类型
  const [chartType, setChartType] = useState<ChartType>(chartTypeUrl);
  // 是否加载可视化数据中
  const [visualisLoading, setVisualisLoading] = useState(true);
  // 柱状图、饼图数据
  const [barOrPieData, setBarOrPieData] = useState<any[]>([]);
  // 折线图数据
  const [lineData, setLineData] = useState<any[]>([]);
  // 宽表总数据条数（搜索前的缓存值，用于清空搜索后恢复数据）
  const tableTotalCached = useRef(0);
  // 宽表总数据条数（搜索前的缓存值，用于清空搜索后恢复数据）
  const tableDataCached = useRef<E[]>([]);
  // 宽表详情数据（搜索钱的缓存值，用于清空搜索后恢复数据）
  const tableDetailsMappingCached = useRef<IRecord<IRecord>>({});
  // 是否加载宽表数据中
  const [tableLoading, setTableLoading] = useState(true);
  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<E[]>([]);
  // 宽表勾选数据（默认值）
  const [tableSelectionDefault, setTableSelectionDefault] = useState<TableSelectionType[]>([]);
  // 宽表勾选数据（当前值）
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  // 是否加载游戏详情数据中
  const [gameDetailsLoading, setGameDetailsLoading] = useState(true);
  // 游戏详情数据
  const [gameDetailsMapping, setGameDetailsMapping] = useState<IRecord<IRecord>>({});
  // 折线图x轴线的dates列表
  const [dates, setDates] = useState<string[]>([]);

  // 宽表是否无数据
  const isNodata = useMemo<boolean>(() => tableTotal < 1 || tableSelection.length < 1, [tableTotal, tableSelection]);

  const updateGetGameData = (
    list: IMetricDataGame[],
    pageParams: any,
    isCheckOrSearch: boolean,
    isUpdateLineChart = true,
  ) => {
    let checkGameList = tableSelection;
    if (pageParams.page === 1) {
      // 过滤rank==0后的游戏可勾选展示可视化
      // @ts-ignore
      const filterNoRankGameList = filterGamesWithRank(list);
      // 第一页时拿到默认前6个游戏作为游戏全选值, 筛选掉被固定的值
      const filterSelector = filterNoRankGameList
        .slice(0, TOPGAMENUM)
        ?.filter((t) => !fixedSelection?.includes(t?.[primaryKey]));
      // 固定选中的要一直被选中，将它的信息过滤出来
      const fixedSelectionGameMes: TableSelectionType[] = filterNoRankGameList?.filter((t) =>
        fixedSelection?.includes(t?.[primaryKey]),
      );
      // 过滤掉选中中有固定选中的，再合并固定的值，取TOPGAMENUM个后按rank排序
      const defaultSelectGames = [...fixedSelectionGameMes, ...filterSelector]
        ?.slice(0, TOPGAMENUM)
        ?.sort((a, b) => a?.rank - b?.rank);
      // 将实际被选中的值做游戏选择处理添加game_id
      const defaultGames = handleTableDefaultSelectGames(defaultSelectGames, 'id');
      setTableSelectionDefault(defaultGames);
      // 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新）
      if (!isCheckOrSearch) {
        setTableSelection(defaultGames);
        // 分页排序请求接口时，可视化不改变
        const barOrPieDataTmp = getTopList(defaultGames, { start: 0, count: TOPGAMENUM });
        setBarOrPieData([...barOrPieDataTmp]);
        checkGameList = defaultGames;
      }
    }
    if (chartType === ChartType.Line && isUpdateLineChart) {
      if (checkGameList.length > 0) {
        const idList = checkGameList.map((t) => t?.gameId);
        getLineChartData(pageParams, idList);
      } else {
        setLineData([]);
      }
    }
  };

  const updateGameDetailsData = async (isSearch: boolean, list?: any[] | null) => {
    if (isEmpty(list)) {
      setGameDetailsLoading(false);
      setGameDetailsMapping({});
      if (!isSearch) {
        tableDetailsMappingCached.current = {};
      }
      return;
    }

    try {
      setGameDetailsLoading(true);
      const ids = list!.map((item) => item?.game_id);
      const res = await getGameInfoByIdPlus({
        entity_type: 'mobile',
        ids,
        // level: 'full',
        level: 'custom',
        custom_fields: [
          'country_abbre',
          'publisher',
          'b_ogdb_data',
          'iegg_genre',
          'iegg_sub_genre',
          'genre',
          'sub_genre',
          'sensortower_genre',
          'sensortower_sub_genre',
          ...MOBILE_TOPCHARTS_TAG_DETAIL_FIELDS,
          /** App Magic 一级品类 */
          'super_genre',
          /** App Magic 二级品类 */
          'major_category',
          /** App Magic 三级品类 */
          'mid_category',
          /** App Magic 四级品类 */
          'sub_category',
          /** App Magic 五级品类 */
          'minor_category',
          'is_special_release_time',
          'release_time',
          'release_time_en',
          'release_time_cn',
          'game_engine',
        ],
      });
      setGameDetailsMapping(res?.data || {});
      if (!isSearch) {
        tableDetailsMappingCached.current = res?.data || {};
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGameDetailsLoading(false);
    }
  };

  const getGameData = async (
    argums: {
      params: any;
      isCheckOrSearch: boolean;
      isUpdateLineChart: boolean;
      updateFilterValues: any;
      filterSaveName?: string;
    },
    idList?: string[],
  ) => {
    const {
      params,
      isCheckOrSearch,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      filterSaveName,
    } = argums;
    // 当时间为空时，等时间返回再更新，否则直接写 token 到接口
    const isUpdateShortLinkNow = !!params?.start_date && !!params?.end_date;
    // 时间如果是空的，则需要更新时间筛选器
    const needUpdateTime = !params?.start_date || !params?.end_date;
    if (isUpdateShortLinkNow && hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }
    try {
      setTableData([]);
      setFetchParams(params);
      const isSearch = params?.game_code?.length > 0;
      const res = await fetchMetricDataGames(convertParams(metrics, params, false, isSearch), {
        params: { _at: IntelligenceRequestAt.Data },
      });
      const { total, list } = res?.data || {};

      updateGameDetailsData(isSearch, list);

      let checkGameList = tableSelection;

      if (params.page === 1) {
        // 过滤 rank==0 后的游戏可勾选展示可视化
        // @ts-ignore
        const filterNoRankGameList = filterGamesWithRank(list);
        const topGameList = getTopList(filterNoRankGameList, { start: 0, count: defaultSelectGameNum });
        // 第一页时拿到默认前 6 个游戏作为游戏全选值
        const defaultGames = handleTableDefaultSelectGames(topGameList, 'game_id');
        setTableSelectionDefault(defaultGames);
        // 在第一页且没有勾选游戏时默认选中前 6（游戏在排序或重新筛选时有更新）
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          setBarOrPieData(topGameList);

          checkGameList = defaultGames;
        }

        // 有继承游戏（Active Users、Revenue、Downloads三个榜单落地页之间切换时，前提用户手动修改过默认勾选游戏）
        if (Array.isArray(idList)) {
          const filterNoRankGameListNew = filterNoRankGameList?.filter((game) => idList.includes(game?.game_id)) || [];
          if (!filterNoRankGameListNew.length) {
            onNoGameExtend?.(topGameList, defaultGames);
          } else {
            const tableSelectionTmp = handleTableDefaultSelectGames(filterNoRankGameListNew, 'game_id');
            setTableSelection(tableSelectionTmp);
            setBarOrPieData(filterNoRankGameListNew);
            checkGameList = tableSelectionTmp;
          }
        }
      }
      if (!isSearch) {
        // 有 game_code 表示是搜索，则不存页面数据
        tableTotalCached.current = total ?? list?.length ?? 0;
        // @ts-ignore
        tableDataCached.current = list || [];
      }
      const {
        granularity,
        start_date: startDate,
        end_date: endDate,
        order_by: orderBy,
        game_ids: gameIds,
      } = res?.req || {};
      const granularityTmp = granularity || params?.date_type || 'daily';
      const { metric, sortType } = analysisSortMetricKey(orderBy?.[0])!;
      const ratioType = !!metric?.ratio ? 1 : 0;
      const orderMetric = (() => {
        const orderMetricTmp = metric?.metric || '';
        return orderMetricTmp !== 'au' ? orderMetricTmp : getGranularityAUMetricKey(granularityTmp);
      })();
      const newFetchParams = {
        ...params,
        date_type: granularityTmp,
        start_date: startDate || params?.start_date || '',
        end_date: endDate || params?.end_date || '',
        order: sortType || params?.order,
        order_source: metric?.dataSource || params?.order_source,
        order_metric: orderMetric || params?.order_metric,
        ratio_type: ratioType || params?.ratio_type,
        filter_ids: isSearch ? params?.filter_ids : gameIds,
      };
      setFetchParams(newFetchParams);

      if (sortByFront) {
        const { tableList: sortList } = handleSortByFront({
          list,
          params: newFetchParams,
          isCheckGame: isCheckOrSearch,
          isUpdateLineChart,
        });
        setTableData(sortList);
        setTableTotal(sortList?.length);
      } else {
        // @ts-ignore
        setTableTotal(total || list?.length);
        // @ts-ignore
        setTableData(list || []);
      }

      // 获取到时间后更新筛选器的值，并更新 url 短链接
      if (needUpdateTime && startDate && endDate) {
        const newFilterValue = updateTimeFilterValue({
          // hasUrlShortLinkVal,
          haveFilterSaveBtn: true,
          filterSaveName,
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: newFetchParams.date_type,
            value: [newFetchParams.start_date, newFetchParams.end_date],
          },
        });
        setFilterVale(newFilterValue);
        // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
        if (!isUpdateShortLinkNow && hasUrlShortLink) {
          updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue });
        }
      }

      if (chartType === ChartType.Line && isUpdateLineChart) {
        if (checkGameList.length > 0) {
          const idList = checkGameList.map((t) => t?.gameId);
          getLineChartData(params, idList);
        }
      }
    } catch (e) {
      console.error(e);
      setTableData([]);
      setBarOrPieData([]);
      setLineData([]);
      setTableSelection([]);
    } finally {
    }
  };

  const getLineChartData = async (params: any, idList: string[]) => {
    // 未勾选任何游戏，不请求，返回为空数据
    if (!Array.isArray(idList) || idList.length === 0) {
      setLineData([]);
      return;
    }

    try {
      setLineData([]);
      const res = await fetchMetricDataGames(
        convertParams(metrics, { ...params, ...PAGINATION_FETCH_PARAMS, game_code: idList }, true),
        {
          params: { _at: IntelligenceRequestAt.Chart },
        },
      );
      const list = res?.data?.list || [];
      const dates = getDatesByDateRange({
        granularity: params?.date_type || 'daily',
        start_date: params?.start_date,
        end_date: params?.end_date,
      });
      setLineData(list || []);
      setDates(dates);
      onGetLineChartDataSuccess?.();
    } catch (e) {
      console.error(e);
      setLineData([]);
      setDates([]);
    } finally {
    }
  };

  const handleSortByFront = (args: {
    params: any;
    isCheckGame: boolean;
    isUpdateLineChart: boolean;
    list: IMetricDataGame[] | null;
  }) => {
    const { list = tableData, params, isCheckGame, isUpdateLineChart = true } = args;

    setFetchParams(params);
    const sortParams = {
      ratioType: params?.ratio_type,
      sortVal: params?.order,
      sortMetric: params?.order_metric,
      sortSource: params?.order_source,
    };

    const getMetacriticValueFun = (cvm: IMetricDataGame) => {
      const metricsList = Array.isArray(cvm?.metric_list) ? cvm?.metric_list : [];
      const metricHistory = metricsList.find((item) => item?.metric === 'metacritic' && item?.title === 'meta_score');
      return metricHistory?.history?.find((item: any) => item?.device === params?.order_device);
    };
    const isSortMetric = sortParams.sortMetric === 'meta_score' && params.order_source === 'metacritic';
    const sortTable = sortTableByMetric({
      // @ts-ignore
      tableData: list,
      sortParams,
      growthProperty: 'growth_ratio',
      listProperty: 'metric_list',
      metricProperty: 'metric',
      sourceProperty: 'source',
      // 评分的排序比较特殊
      getMetricValueFun: isSortMetric ? getMetacriticValueFun : undefined,
    });
    // 建议没有排名的放在最后
    const tableUpdateRank = sortTable?.map((t, idx) => ({ ...t, rank: idx + 1 }));
    updateGetGameData(tableUpdateRank, params, isCheckGame, isUpdateLineChart);
    setTableData(tableUpdateRank);
    // 前端排序后的Id的回填到参数用于有序下载
    const newFetchParams = {
      ...params,
      filter_ids: tableUpdateRank?.map((t) => t?.[primaryKey]),
    };
    setFetchParams(newFetchParams);
    return { tableList: tableUpdateRank, params: newFetchParams };
  };

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, game_code: [] });
    setTableTotal(tableTotalCached.current);
    setTableData(tableDataCached.current);
    setGameDetailsMapping(tableDetailsMappingCached.current || {});
  };

  useEffect(() => {
    setChartType(chartTypeUrl);
  }, [chartTypeUrl]);

  return {
    isNodata,
    fetchParams,
    chartType,
    setChartType,
    visualisLoading,
    setVisualisLoading,
    barOrPieData,
    setBarOrPieData,
    lineData,
    tableLoading,
    setTableLoading,
    tableTotal,
    tableData,
    tableSelectionDefault,
    tableSelection,
    setTableSelection,
    gameDetailsLoading,
    gameDetailsMapping,
    dates,
    getGameData,
    getLineChartData,
    handleClearSearch,
  };
}
